import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Calendar, ChevronDown, Check } from 'lucide-react'
// import { useClickOutside } from '../../hooks/useClickOutside'
import { paymentAssets, controlIcons } from '../../constants/assets'

export type DateRangePreset = {
  id: string
  label: string
  from?: string
  to?: string
}

export const getDynamicDatePresets = (): DateRangePreset[] => {
  const now = new Date()
  const todayStr = now.toISOString().slice(0, 10)

  // Start & end of this week
  const startOfWeek = new Date(now)
  startOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1))
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6)
  const weekStartStr = startOfWeek.toISOString().slice(0, 10)
  const weekEndStr = endOfWeek.toISOString().slice(0, 10)

  // Start & end of this month
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const monthStartStr = monthStart.toISOString().slice(0, 10)
  const monthEndStr = monthEnd.toISOString().slice(0, 10)

  // Last 30 days
  const past30 = new Date(now)
  past30.setDate(now.getDate() - 30)
  const past30Str = past30.toISOString().slice(0, 10)

  return [
    { id: 'all', label: 'All Dates' },
    { id: 'today', label: 'Today', from: todayStr, to: todayStr },
    { id: 'this-week', label: 'This Week', from: weekStartStr, to: weekEndStr },
    { id: 'this-month', label: 'This Month', from: monthStartStr, to: monthEndStr },
    { id: 'last-30', label: 'Last 30 Days', from: past30Str, to: todayStr },
  ]
}

type DateRangePickerProps = {
  value: string
  onChange: (value: string, from?: string, to?: string) => void
  label?: string
  variant?: 'field' | 'button'
  height?: number
  width?: number
}
type PopoverPosition = {
  placement: 'above' | 'below'
  offset: number
  left: number
  width: number
  maxHeight: number
}
const formatDateLabel = (isoDate: string) => {
  if (!isoDate) return ''
  const parts = isoDate.split('-')
  if (parts.length < 3) return isoDate
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[parseInt(parts[1], 10) - 1] || parts[1]
  return `${parseInt(parts[2], 10)} ${month} ${parts[0]}`
}

export const DateRangePicker = ({
  value,
  onChange,
  label = 'Date Range',
  variant = 'field',
  height = variant === 'field' ? 65 : 36,
  width,
}: DateRangePickerProps) => {
 const [open, setOpen] = useState(false)

const containerRef = useRef<HTMLDivElement>(null)
const panelRef = useRef<HTMLDivElement>(null)
const listId = useId()

const [customStart, setCustomStart] = useState('')
const [customEnd, setCustomEnd] = useState('')

const [panelPosition, setPanelPosition] =
  useState<PopoverPosition>({
    placement: 'below',
    offset: 0,
    left: 0,
    width: 300,
    maxHeight: 480,
  })

  const updatePanelPosition = useCallback(() => {
  const trigger = containerRef.current

  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const viewportPadding = 12
  const panelGap = 8

  const panelWidth = Math.min(
    Math.max(rect.width, 300),
    window.innerWidth - viewportPadding * 2,
  )

  const left = Math.min(
    Math.max(viewportPadding, rect.right - panelWidth),
    window.innerWidth - panelWidth - viewportPadding,
  )

  const spaceBelow =
    window.innerHeight -
    rect.bottom -
    panelGap -
    viewportPadding

  const spaceAbove =
    rect.top -
    panelGap -
    viewportPadding

  const placement: PopoverPosition['placement'] =
    spaceBelow >= 420 || spaceBelow >= spaceAbove
      ? 'below'
      : 'above'

  const availableHeight =
    placement === 'below' ? spaceBelow : spaceAbove

  setPanelPosition({
    placement,
    offset:
      placement === 'below'
        ? rect.bottom + panelGap
        : window.innerHeight - rect.top + panelGap,
    left,
    width: panelWidth,
    maxHeight: Math.min(520, availableHeight),
  })
}, [])

const handleToggle = () => {
  if (!open) {
    updatePanelPosition()
  }

  setOpen((current) => !current)
}

useEffect(() => {
  if (!open) return

  const handlePointerDown = (event: PointerEvent) => {
    const target = event.target as Node

    const clickedTrigger =
      containerRef.current?.contains(target)

    const clickedPanel =
      panelRef.current?.contains(target)

    if (!clickedTrigger && !clickedPanel) {
      setOpen(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  document.addEventListener('pointerdown', handlePointerDown)
  document.addEventListener('keydown', handleKeyDown)

  return () => {
    document.removeEventListener(
      'pointerdown',
      handlePointerDown,
    )
    document.removeEventListener('keydown', handleKeyDown)
  }
}, [open])

useEffect(() => {
  if (!open) return

  updatePanelPosition()

  window.addEventListener('resize', updatePanelPosition)
  window.addEventListener(
    'scroll',
    updatePanelPosition,
    true,
  )

  return () => {
    window.removeEventListener(
      'resize',
      updatePanelPosition,
    )
    window.removeEventListener(
      'scroll',
      updatePanelPosition,
      true,
    )
  }
}, [open, updatePanelPosition])

  const presets = useMemo(() => getDynamicDatePresets(), [])

  // Determine display label
  const displayLabel = useMemo(() => {
    if (!value || value === 'all') return 'All Dates'

    const matchedPreset = presets.find((p) => p.id === value)
    if (matchedPreset) return matchedPreset.label

    if (value.startsWith('custom:')) {
      const dates = value.replace('custom:', '').split('_')
      if (dates[0] && dates[1]) {
        return `${formatDateLabel(dates[0])} — ${formatDateLabel(dates[1])}`
      }
      if (dates[0]) return `From ${formatDateLabel(dates[0])}`
    }

    // Handle legacy June-2026 format if present
    if (value.includes('jun-2026')) return '01 Jun – 30 Jun 2026'
    if (value.includes('jul-2026')) return '01 Jul – 31 Jul 2026'
    if (value.includes('aug-2026')) return '01 Aug – 31 Aug 2026'

    return value
  }, [value, presets])

  const handleApplyCustom = () => {
    if (!customStart) return
    const end = customEnd || customStart
    const customValue = `custom:${customStart}_${end}`
    onChange(customValue, customStart, end)
    setOpen(false)
  }

  const isFieldVariant = variant === 'field'

  return (
    <div
  ref={containerRef}
  className={
    isFieldVariant
      ? 'relative w-full'
      : 'relative w-fit'
  }
>
      {isFieldVariant ? (
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={handleToggle}
          style={{ height }}
          className="flex w-full items-center justify-between gap-[12px] rounded-[12px] border border-solid border-dash-field bg-white px-[18px] transition hover:border-[#8EAA4D]"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Calendar size={18} className="text-[#FF6A1A] shrink-0" />
            <span className="truncate text-left font-host text-[15px] font-medium leading-normal text-dash-field-text">
              {displayLabel}
            </span>
          </div>
          <img
            alt=""
            aria-hidden
            src={controlIcons.selectCaret}
            className={`block size-[28px] max-w-none shrink-0 object-cover transition-transform duration-200 ${
              open ? '-scale-y-100' : ''
            }`}
          />
        </button>
      ) : (
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          style={{ height, width: width || (value && value !== 'all' ? 180 : 130) }}
          className="flex items-center justify-center gap-[8px] rounded-[8px] border border-solid border-dash-cell-faint bg-white px-3 transition hover:border-[#8EAA4D]"
        >
          <img
            alt=""
            aria-hidden
            src={paymentAssets.calendar}
            className="block size-[20px] max-w-none shrink-0"
          />
          <span className="truncate font-host text-[13px] font-medium leading-normal text-black">
            {displayLabel}white
          </span>
          <ChevronDown size={14} className="text-gray-500 shrink-0" />
        </button>
      )}

      {open &&
  typeof document !== 'undefined' &&
  createPortal(
    <div
      ref={panelRef}
      id={listId}
      style={{
        left: panelPosition.left,
        width: panelPosition.width,
        maxHeight: panelPosition.maxHeight,
        ...(panelPosition.placement === 'below'
          ? { top: panelPosition.offset }
          : { bottom: panelPosition.offset }),
      }}
      className="fixed z-[1000] flex flex-col overflow-hidden rounded-[14px] border border-[#D6D6D6] bg-white text-left shadow-[0_18px_50px_rgba(15,23,42,0.22)] ring-1 ring-black/5"
    >
      {/* Scrollable section */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white px-4 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <p className="mb-1.5 font-host text-[12px] font-semibold uppercase tracking-wider text-gray-400">
          Quick Presets
        </p>

        <div className="mb-3 flex flex-col gap-0.5">
          {presets.map((preset) => {
  const isSelected = value === preset.id

  return (
    <button
      key={preset.id}
      type="button"
      onClick={() => {
        onChange(
          preset.id,
          preset.from,
          preset.to,
        )
        setOpen(false)
      }}
      className={`flex items-center justify-between rounded-lg px-3 py-1.5 text-left font-host text-[13px] transition ${
        isSelected
          ? 'bg-[#FF6A1A]/10 font-semibold text-[#FF6A1A]'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span>{preset.label}</span>

      {isSelected && (
        <Check
          size={15}
          className="text-[#FF6A1A]"
        />
      )}
    </button>
  )
})}
        </div>

        <div className="border-t border-gray-100 pt-3">
          <p className="mb-2.5 font-host text-[12px] font-semibold uppercase tracking-wider text-gray-400">
            Custom Date Range
          </p>

          <div className="space-y-2">
            <div>
              <label className="mb-1 block font-host text-[11px] text-gray-500">
                Start Date
              </label>

              <input
                type="date"
                value={customStart}
                onChange={(event) =>
                  setCustomStart(event.target.value)
                }
                className="w-full rounded-lg border border-gray-300 p-2 font-host text-[12px] text-gray-800 outline-none focus:border-[#FF6A1A]"
              />
            </div>

            <div>
              <label className="mb-1 block font-host text-[11px] text-gray-500">
                End Date
              </label>

              <input
                type="date"
                value={customEnd}
                onChange={(event) =>
                  setCustomEnd(event.target.value)
                }
                className="w-full rounded-lg border border-gray-300 p-2 font-host text-[12px] text-gray-800 outline-none focus:border-[#FF6A1A]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fixed action footer */}
      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 py-2.5 shadow-[0_-6px_16px_rgba(15,23,42,0.05)]">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-lg px-3 py-1.5 font-host text-[12px] text-gray-500 transition hover:bg-gray-100"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={!customStart}
          onClick={handleApplyCustom}
          className="rounded-lg bg-[#FF6A1A] px-4 py-1.5 font-host text-[12px] font-medium text-white transition hover:bg-[#e55910] disabled:opacity-40"
        >
          Apply
        </button>
      </div>
    </div>,
    document.body,
  )}
    </div>
  )
}
